import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { shortUuid, type Container } from "@egalregal/shared";
import { getMirroredItems } from "../search/catalogStore";
import { requests } from "../../shared/api";
import { useAppStore } from "../../shared/store/app";
import {
  SCAN_HISTORY_LIMIT,
  selectScanHistoryEntries,
  useScanHistoryStore,
  type ScanHistoryEntry,
} from "../../shared/store/scanHistory";
import { useOpenContainer } from "../../shared/store/useOpenContainer";
import { useT } from "../../shared/i18n";
import { log } from "../../logger";
import { CloseIcon } from "../../shared/components/icons";
import { createResolveDeps } from "./resolution";
import { resolveScanHistoryTarget, type ScanHistoryTarget } from "./scanHistoryTarget";

interface ScanHistoryOverlayProps {
  open: boolean;
  onClose: () => void;
  /**
   * Load a recalled code into the scanner. The surface re-resolves it live, so
   * the stored outcome snapshot is never what the user sees (results-05).
   */
  onLoad: (entry: ScanHistoryEntry) => void;
  /**
   * Open a stored payload (URL, Wi-Fi or text) in the scanner's payload view
   * (results-06). The stored payload is used as-is so the recalled view and
   * actions match the live result.
   */
  onOpenPayload: (entry: ScanHistoryEntry) => void;
}

/**
 * The browser-local scan history as an overlay over the scanner (results-05):
 * a right drawer on `md+` and a bottom sheet on phones. Rows are the last
 * {@link SCAN_HISTORY_LIMIT} scans, newest first.
 *
 * A row tap opens its **live** target — the container with the scan origin, the
 * item detail, or the payload view. A target that no longer resolves (deleted,
 * trashed, foreign, unknown) is marked and the row loads the code into the
 * scanner instead of navigating to a dead page. `Load` always re-resolves into
 * the scanner and never renders the stored snapshot.
 */
export function ScanHistoryOverlay({
  open,
  onClose,
  onLoad,
  onOpenPayload,
}: ScanHistoryOverlayProps) {
  const t = useT();
  const theme = useTheme();
  const navigate = useNavigate();
  const openContainer = useOpenContainer();
  const bottomSheet = useMediaQuery(theme.breakpoints.down("md"));
  const entries = useScanHistoryStore(selectScanHistoryEntries);
  const containers = useAppStore((state) => state.containers);
  const trash = useAppStore((state) => state.trash);
  const clearHistory = useScanHistoryStore((state) => state.clearHistory);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const resolverDeps = useMemo(() => createResolveDeps(), []);
  // Each row opens and titles from the same cache-first resolution the scanner
  // uses (scan-resolution 03). The map starts empty, so a row renders its
  // stored snapshot until its live target lands.
  const [targets, setTargets] = useState<ReadonlyMap<string, ScanHistoryTarget>>(
    () => new Map(),
  );

  const visible = entries.slice(0, SCAN_HISTORY_LIMIT);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void Promise.all(
      entries.slice(0, SCAN_HISTORY_LIMIT).map(async (entry) => {
        try {
          const target = await resolveScanHistoryTarget(
            entry,
            { containers, trash },
            resolverDeps,
          );
          return [entry.id, target] as const;
        } catch (err) {
          // An unexpected answer is not a domain result: keep the row loadable
          // instead of failing the whole overlay.
          log.warn({ err, code: entry.code }, "[scan] Resolving a history target failed");
          return [entry.id, { kind: "none" } as ScanHistoryTarget] as const;
        }
      }),
    ).then((pairs) => {
      if (!cancelled) setTargets(new Map(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [open, entries, containers, trash, resolverDeps]);

  const load = (entry: ScanHistoryEntry) => {
    onClose();
    onLoad(entry);
  };

  const openRow = async (entry: ScanHistoryEntry, target?: ScanHistoryTarget) => {
    let resolved = target;
    if (!resolved) {
      try {
        resolved = await resolveScanHistoryTarget(entry, { containers, trash }, resolverDeps);
      } catch (err) {
        // An unexpected answer is not a domain result: never leave the tap an
        // unhandled rejection. The stored-snapshot path keeps the row usable.
        log.warn({ err, code: entry.code }, "[scan] Resolving a history row failed");
        load(entry);
        return;
      }
    }
    if (resolved.kind === "container" && !resolved.trashed) {
      onClose();
      void openContainer(resolved.id, { from: "scan" });
      return;
    }
    if (resolved.kind === "item") {
      onClose();
      navigate(`/items/${resolved.id}`, { state: { from: "scan" } });
      return;
    }
    if (resolved.kind === "payload") {
      onClose();
      onOpenPayload(entry);
      return;
    }
    // Deleted/trashed/foreign/offline/unknown targets: load the code instead of
    // opening a dead page. The scanner owns the offline panel.
    load(entry);
  };

  const confirmClear = () => {
    clearHistory();
    setConfirmingClear(false);
  };

  return (
    <>
      <Drawer
        anchor={bottomSheet ? "bottom" : "right"}
        open={open}
        onClose={onClose}
        transitionDuration={0}
        PaperProps={{
          role: "dialog",
          "aria-modal": true,
          "aria-label": t("scanHistoryTitle"),
          "data-testid": "scan-history-dialog",
          "data-anchor": bottomSheet ? "bottom" : "right",
          sx: {
            width: bottomSheet ? "100%" : 400,
            maxHeight: bottomSheet ? "78dvh" : "100%",
            display: "flex",
            flexDirection: "column",
            borderTopLeftRadius: bottomSheet ? 16 : 0,
            borderTopRightRadius: bottomSheet ? 16 : 0,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 700 }}>
            {t("scanHistoryTitle")}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={t("scanHistoryCount", {
              count: String(visible.length),
              limit: String(SCAN_HISTORY_LIMIT),
            })}
          />
          <IconButton size="small" aria-label={t("scanHistoryClose")} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
            {t("scanHistoryHint")}
          </Typography>
          <Button
            size="small"
            color="error"
            disabled={visible.length === 0}
            onClick={() => setConfirmingClear(true)}
            data-testid="scan-history-clear"
          >
            {t("scanHistoryClear")}
          </Button>
        </Stack>

        {open && visible.length > 0 && targets.size === 0 && (
          <LinearProgress data-testid="scan-history-resolving" sx={{ mx: 1.5 }} />
        )}

        {visible.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="scan-history-empty"
            sx={{ px: 2, py: 5, textAlign: "center" }}
          >
            {t("scanHistoryEmpty")}
          </Typography>
        ) : (
          <List dense disablePadding sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto", px: 0.5, pb: 1 }}>
            {visible.map((entry) => {
              const target = targets.get(entry.id);
              const title = titleFor(entry, target, t);
              const code = entry.payload.kind === "label" ? shortUuid(entry.code) : entry.code;
              const path =
                target?.kind === "container" || target?.kind === "item" ? target.path : undefined;
              const photo = previewPhotoFor(target, containers, trash);
              return (
                <ListItemButton
                  key={entry.id}
                  data-testid="scan-history-row"
                  onClick={() => void openRow(entry, target)}
                  sx={{ borderRadius: 1.5, alignItems: "flex-start", gap: 1, py: 1 }}
                >
                  <HistoryThumb
                    key={photo ?? "glyph"}
                    photo={photo}
                    glyph={glyphFor(entry, title, t)}
                    name={title}
                  />
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      data-testid="scan-history-title"
                      sx={{ fontWeight: 600 }}
                    >
                      {title}
                    </Typography>
                    {path && path !== title && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        title={path}
                        data-testid="scan-history-path"
                        sx={{ display: "block" }}
                      >
                        {path}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      title={code}
                      data-testid="scan-history-code"
                      sx={{ display: "block", fontFamily: "monospace" }}
                    >
                      {code}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      noWrap
                      data-testid="scan-history-meta"
                      sx={{ display: "block" }}
                    >
                      {statusLine(entry, target, t)}
                      {entry.count > 1 ? ` · ×${entry.count}` : ""}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    data-testid="scan-history-load"
                    onClick={(event) => {
                      event.stopPropagation();
                      load(entry);
                    }}
                  >
                    {t("scanHistoryLoad")}
                  </Button>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Drawer>

      <Dialog
        open={confirmingClear}
        onClose={() => setConfirmingClear(false)}
        transitionDuration={0}
        aria-labelledby="scan-history-clear-title"
      >
        <DialogTitle id="scan-history-clear-title">{t("scanHistoryClearTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("scanHistoryClearBody")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingClear(false)}>{t("cancel")}</Button>
          <Button color="error" variant="contained" onClick={confirmClear} data-testid="scan-history-clear-confirm">
            {t("scanHistoryClear")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** The row's preview thumbnail; a missing/broken image degrades to the glyph. */
function HistoryThumb({
  photo,
  glyph,
  name,
}: {
  photo?: string;
  glyph: string;
  name: string;
}) {
  const [broken, setBroken] = useState(false);
  if (photo && !broken) {
    return (
      <Box
        component="img"
        data-testid="scan-history-photo"
        src={photo}
        alt=""
        aria-hidden
        onError={() => setBroken(true)}
        sx={{ width: 44, height: 44, borderRadius: 1, objectFit: "cover", flexShrink: 0 }}
      />
    );
  }
  return (
    <Box
      data-testid="scan-history-glyph"
      aria-hidden
      title={name}
      sx={{
        width: 44,
        height: 44,
        borderRadius: 1,
        bgcolor: "action.hover",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: "0.68rem",
        color: "text.secondary",
        flexShrink: 0,
      }}
    >
      {glyph}
    </Box>
  );
}

/** The row's primary label: live target name, payload kind, or a label fallback. */
function titleFor(
  entry: ScanHistoryEntry,
  target: ScanHistoryTarget | undefined,
  t: ReturnType<typeof useT>,
): string {
  if (target?.kind === "container" || target?.kind === "item") return target.label;
  if (entry.payload.kind === "url") return t("scanHistoryPayloadUrl");
  if (entry.payload.kind === "wifi") {
    const ssid = entry.payload.ssid;
    return ssid
      ? t("scanHistoryPayloadWifi", { ssid })
      : t("scanHistoryPayloadWifiGeneric");
  }
  if (entry.payload.kind === "text") return t("scanHistoryPayloadText");
  if (entry.payload.kind === "label") return entry.outcome?.label ?? t("scanHistoryUnknownLabel");
  return entry.outcome?.label ?? entry.code;
}

/** The meta line: availability, then the relative time. */
function statusLine(
  entry: ScanHistoryEntry,
  target: ScanHistoryTarget | undefined,
  t: ReturnType<typeof useT>,
): string {
  const time = relativeTime(entry.at, t);
  if (target?.kind === "container" && target.trashed) {
    return `${t("scanHistoryTrashed")} · ${time}`;
  }
  if (target?.kind === "offline") {
    return `${t("scanHistoryOffline")} · ${time}`;
  }
  if (target?.kind === "none") return `${t("scanHistoryUnavailable")} · ${time}`;
  return time;
}

/** The offline-safe preview: container's first photo or the item's hero photo. */
function previewPhotoFor(
  target: ScanHistoryTarget | undefined,
  containers: readonly Container[],
  trash: readonly Container[],
): string | undefined {
  if (target?.kind === "container") {
    const host = [...containers, ...trash].find((candidate) => candidate.id === target.id);
    const photo = host?.files.find((file) => file.kind === "photo");
    return photo ? requests.media.previewUrl(host!.id, photo.id) : undefined;
  }
  if (target?.kind === "item") {
    const mirrored = getMirroredItems().find((candidate) => candidate.id === target.id);
    const local = mirrored?.photos[0];
    if (mirrored && local) return requests.media.itemPhotoPreviewUrl(mirrored.id, local.id);
    return mirrored?.photo ?? undefined;
  }
  // Payload entries never load an image from their content (results-06).
  return undefined;
}

/** The fallback glyph: payload kind, or the label's first two letters. */
function glyphFor(
  entry: ScanHistoryEntry,
  title: string,
  t: ReturnType<typeof useT>,
): string {
  if (entry.payload.kind === "url") return t("scanHistoryGlyphUrl");
  if (entry.payload.kind === "wifi") return t("scanHistoryGlyphWifi");
  if (entry.payload.kind === "text") return t("scanHistoryGlyphText");
  return title.trim().slice(0, 2).toUpperCase() || "?";
}

/** Compact relative time; the history stores epoch milliseconds (results-04). */
function relativeTime(at: number, t: ReturnType<typeof useT>): string {
  const minutes = Math.max(0, Math.round((Date.now() - at) / 60_000));
  if (minutes < 1) return t("scanHistoryJustNow");
  if (minutes < 60) return t("scanHistoryMinutesAgo", { count: String(minutes) });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t("scanHistoryHoursAgo", { count: String(hours) });
  return t("scanHistoryDaysAgo", { count: String(Math.round(hours / 24)) });
}
